import "./ReportSchedule.scss";

import React from "react";
import usesFeatures from "usesFeatures";

import request from "request";

import Form from "Form";
import Header from "Header";
import Input from "Input";
import Select from "Select";
import Checkbox from "Checkbox";

const REPORTS = [
  { key: "sales",    label: "Sales report",    note: "Includes the day's sales for the event scheduled today. If no event is on the calendar, no email is sent." },
  { key: "payments", label: "Payments report", note: "Card / cash / tip breakdown by terminal for the event scheduled today." },
  { key: "timeLog",  label: "Time logs",       note: "All time punches from the last 24 hours. If nobody clocked in, no email is sent." },
];

const HOUR_OPTIONS = Object.fromEntries(
  Array.from({ length: 24 }, (_, h) => [String(h), `${h.toString().padStart(2, "0")}:00`])
);

const MINUTE_OPTIONS = Object.fromEntries(
  Array.from({ length: 12 }, (_, i) => {
    const m = i * 5;
    return [String(m), m.toString().padStart(2, "0")];
  })
);

function joinCc(list) {
  return Array.isArray(list) ? list.join(", ") : "";
}

function splitCc(text) {
  if (!text) return [];
  return text.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
}

class ReportSchedule extends React.Component {
  state = { saved: false, errors: null, venue: null }

  componentDidMount() {
    this.props.fetchData("venue_admin/report_schedule", (data) => {
      const r = data.reportSchedule || {};
      this.setState({ venue: data.venue || null });
      this.props.seedFormData({
        formData: {
          salesEnabled:    !!r.salesEnabled,
          salesHour:       r.salesHour != null ? String(r.salesHour) : "",
          salesMinute:     r.salesMinute != null ? String(r.salesMinute) : "",
          salesCcText:     joinCc(r.salesCc),
          paymentsEnabled: !!r.paymentsEnabled,
          paymentsHour:    r.paymentsHour != null ? String(r.paymentsHour) : "",
          paymentsMinute:  r.paymentsMinute != null ? String(r.paymentsMinute) : "",
          paymentsCcText:  joinCc(r.paymentsCc),
          timeLogEnabled:  !!r.timeLogEnabled,
          timeLogHour:     r.timeLogHour != null ? String(r.timeLogHour) : "",
          timeLogMinute:   r.timeLogMinute != null ? String(r.timeLogMinute) : "",
          timeLogCcText:   joinCc(r.timeLogCc),
        },
      });
    });
  }

  handleSubmit = () => {
    const { formData } = this.props;

    const payload = {};
    REPORTS.forEach(({ key }) => {
      payload[`${key}Enabled`] = !!formData[`${key}Enabled`];
      payload[`${key}Hour`]    = formData[`${key}Hour`]   === "" ? null : Number(formData[`${key}Hour`]);
      payload[`${key}Minute`]  = formData[`${key}Minute`] === "" ? null : Number(formData[`${key}Minute`]);
      payload[`${key}Cc`]      = splitCc(formData[`${key}CcText`]);
    });

    this.setState({ saved: false, errors: null });

    request.put("venue_admin/report_schedule", { report_schedule: payload })
      .then(() => this.setState({ saved: true, errors: null }))
      .catch((err) => {
        const errors = err?.response?.data?.errors || ["Unable to save report schedule."];
        this.setState({ errors });
      });
  }

  renderReport({ key, label, note }) {
    const { formData, updateField } = this.props;
    const enabled = !!formData[`${key}Enabled`];

    return (
      <div className="ReportSchedule-card s-card block" key={key}>
        <div className="ReportSchedule-card-header">
          <Checkbox
            label={label}
            value={enabled}
            onChange={updateField.bind(null, `${key}Enabled`)}
          />
        </div>

        <p className="ReportSchedule-note">{note}</p>

        <div className="ReportSchedule-time-row">
          <Select
            label="Hour"
            options={HOUR_OPTIONS}
            value={formData[`${key}Hour`]}
            onChange={updateField.bind(null, `${key}Hour`)}
            disabled={!enabled}
            prompt="HH"
          />
          <Select
            label="Minute"
            options={MINUTE_OPTIONS}
            value={formData[`${key}Minute`]}
            onChange={updateField.bind(null, `${key}Minute`)}
            disabled={!enabled}
            prompt="MM"
          />
        </div>

        <Input
          label="CC (comma-separated emails)"
          value={formData[`${key}CcText`] || ""}
          onChange={updateField.bind(null, `${key}CcText`)}
          disabled={!enabled}
          placeholder="finance@example.com, manager@example.com"
        />
      </div>
    );
  }

  render() {
    if (!this.props.dataFetched) return false;

    const { saved, errors, venue } = this.state;
    const zoneLabel = venue?.timeZone || venue?.ianaTimeZone;

    return (
      <div className="ReportSchedule">
        <Header text="Daily Report Emails" />

        <p className="ReportSchedule-zone-banner">
          Times are interpreted in the venue's time zone{zoneLabel ? ` (${zoneLabel})` : ""}.
          Each enabled report is delivered as a separate email with the HTML report in the body and the CSV attached.
        </p>

        <Form onSubmit={this.handleSubmit}>
          {REPORTS.map((r) => this.renderReport(r))}

          {errors && (
            <div className="block notification is-danger">
              {errors.map((e, i) => (<div key={i}>{e}</div>))}
            </div>
          )}

          {saved && (
            <div className="block notification is-success">Report schedule saved.</div>
          )}

          <div className="block field is-grouped">
            <p className="control">
              <button type="button" onClick={this.handleSubmit} className="button is-primary">Save</button>
            </p>
          </div>
        </Form>
      </div>
    );
  }
}

export default usesFeatures(["data", "form"], ReportSchedule);
